/**
 * Tiny lexical search engine.
 *
 * Used by both the build script (to pre-tokenize bodies) and the client
 * runtime (to score queries against the loaded index). Public API:
 *
 *   tokenize(text)            → string[]   — lowercased, stop-words dropped
 *   normalizeBody(text)       → string     — space-joined unique tokens
 *   search(docs, query, opts) → SearchHit[] — ranked best-first
 *
 * Scoring is deliberately simple — title hits dominate, body hits tiebreak,
 * and we layer in a Levenshtein-1 fuzzy fallback for tokens ≥ 4 chars so
 * "feritin" still finds ferritin without an external dependency. Fuzzy
 * matches score ~⅓ of an exact title hit, so they only surface when no
 * exact match exists.
 */

import type { SearchDoc } from "./types"

const STOP_WORDS = new Set([
  // EN
  "a",
  "an",
  "the",
  "and",
  "or",
  "of",
  "to",
  "for",
  "in",
  "on",
  "at",
  "is",
  "are",
  "was",
  "were",
  "be",
  "by",
  "with",
  "as",
  "it",
  "its",
  "this",
  "that",
  "these",
  "those",
  "from",
  "but",
  // DE
  "der",
  "die",
  "das",
  "und",
  "oder",
  "ist",
  "sind",
  "ein",
  "eine",
  "einen",
  "den",
  "dem",
  "des",
  "im",
  "in",
  "an",
  "am",
  "mit",
  "für",
  "von",
  "zu",
  "es",
  "sie",
  "er",
  // FR
  "le",
  "la",
  "les",
  "un",
  "une",
  "des",
  "et",
  "ou",
  "de",
  "du",
  "à",
  "au",
  "aux",
  "ce",
  "ces",
  "est",
  "sont",
  "pour",
  "par",
  "dans",
  "sur",
  "avec",
  // NL
  "de",
  "het",
  "een",
  "en",
  "of",
  "van",
  "voor",
  "in",
  "op",
  "met",
  "is",
  "zijn",
  "te",
  "die",
  "dat",
  "dit",
])

const TOKEN_RE = /[\p{L}\p{N}]+/gu

/** Minimum token length before fuzzy matching kicks in — short tokens have
 *  too many neighbours within edit distance 1 (e.g. "the" ↔ "tee"). */
const FUZZY_MIN_LENGTH = 4

export function tokenize(text: string): string[] {
  if (!text) return []
  const matches = text.toLowerCase().match(TOKEN_RE)
  if (!matches) return []
  const out: string[] = []
  for (const tok of matches) {
    if (tok.length < 2) continue
    if (STOP_WORDS.has(tok)) continue
    out.push(tok)
  }
  return out
}

export function normalizeBody(text: string): string {
  const tokens = tokenize(text)
  if (tokens.length === 0) return ""
  return Array.from(new Set(tokens)).join(" ")
}

/**
 * Returns true when `a` and `b` differ by at most one insert / delete /
 * substitution. Specialized to the |la−lb| ≤ 1 case so it avoids allocating
 * a full DP matrix — for the tens-of-thousands of token comparisons each
 * search query triggers, this matters.
 */
export function withinEditDistance1(a: string, b: string): boolean {
  if (a === b) return true
  const la = a.length
  const lb = b.length
  if (la > lb + 1 || lb > la + 1) return false

  if (la === lb) {
    let diffs = 0
    for (let i = 0; i < la; i++) {
      if (a.charCodeAt(i) !== b.charCodeAt(i)) {
        diffs++
        if (diffs > 1) return false
      }
    }
    return diffs <= 1
  }

  const long = la > lb ? a : b
  const short = la > lb ? b : a
  let i = 0
  let j = 0
  let skipped = false
  while (i < long.length && j < short.length) {
    if (long.charCodeAt(i) === short.charCodeAt(j)) {
      i++
      j++
    } else {
      if (skipped) return false
      skipped = true
      i++ // drop one char from the longer string
    }
  }
  return true
}

/**
 * True if any token in `candidates` is within edit distance 1 of `query` —
 * pruned to candidates with the same first character and length within ±1
 * before running the actual distance check. With ~50 tokens per doc the
 * pruning eliminates ~95% of the candidate set in O(1) per token.
 */
function fuzzyMatchesAny(
  query: string,
  candidates: ReadonlySet<string>
): boolean {
  if (query.length < FUZZY_MIN_LENGTH) return false
  const firstChar = query.charCodeAt(0)
  const qLen = query.length
  for (const candidate of candidates) {
    if (candidate.charCodeAt(0) !== firstChar) continue
    const cLen = candidate.length
    if (cLen < qLen - 1 || cLen > qLen + 1) continue
    if (withinEditDistance1(candidate, query)) return true
  }
  return false
}

export interface SearchHit {
  doc: SearchDoc
  score: number
}

export interface SearchOptions {
  limit?: number
  /** Only include results of these types. Defaults to all. */
  types?: ReadonlyArray<SearchDoc["type"]>
}

/**
 * Score a single doc against a tokenized query. Per query token we award:
 *   +12  exact title token match
 *   + 6  title token starts with query token
 *   + 4  fuzzy title match (≤ 1 edit, ≥ 4 chars)
 *   + 3  query token starts with any title token
 *   + 1  body contains the token (exact)
 *   + 0.5 fuzzy body match (≤ 1 edit, ≥ 4 chars)
 * A short-circuit zero return when no field hits anything, so docs that
 * don't match any token at all get filtered out before the sort.
 */
function scoreDoc(doc: SearchDoc, queryTokens: string[]): number {
  const titleTokens = tokenize(doc.title)
  const titleSet = new Set(titleTokens)
  const bodySet = new Set(doc.bodyTokens.split(" "))

  let score = 0
  let anyHit = false
  for (const qt of queryTokens) {
    let tokenScore = 0
    if (titleSet.has(qt)) {
      tokenScore += 12
    } else {
      let prefixHit = false
      for (const tt of titleTokens) {
        if (tt.startsWith(qt)) {
          tokenScore += 6
          prefixHit = true
          break
        }
        if (qt.startsWith(tt) && tt.length >= 3) {
          tokenScore += 3
          prefixHit = true
          break
        }
      }
      if (!prefixHit && fuzzyMatchesAny(qt, titleSet)) {
        tokenScore += 4
      }
    }
    if (bodySet.has(qt)) {
      tokenScore += 1
    } else if (tokenScore === 0 && fuzzyMatchesAny(qt, bodySet)) {
      // Only check body-fuzzy when nothing else hit — keeps relevance high
      // for queries that already matched in the title.
      tokenScore += 0.5
    }
    if (tokenScore > 0) anyHit = true
    score += tokenScore
  }
  return anyHit ? score : 0
}

export function search(
  docs: ReadonlyArray<SearchDoc>,
  query: string,
  options: SearchOptions = {}
): SearchHit[] {
  const queryTokens = tokenize(query)
  if (queryTokens.length === 0) return []

  const { limit = 24, types } = options
  const typeFilter = types && types.length > 0 ? new Set(types) : null

  const hits: SearchHit[] = []
  for (const doc of docs) {
    if (typeFilter && !typeFilter.has(doc.type)) continue
    const score = scoreDoc(doc, queryTokens)
    if (score > 0) hits.push({ doc, score })
  }
  hits.sort(
    (a, b) => b.score - a.score || a.doc.title.localeCompare(b.doc.title)
  )
  return hits.slice(0, limit)
}

/**
 * Split `text` into runs around the first occurrence of any query token —
 * for highlighting in search-result excerpts. Returns ordered segments
 * with `{ text, match }`; the caller wraps `match: true` runs in `<mark>`.
 *
 * Lower-bar matching (case-insensitive substring) on purpose: this is for
 * VISUAL feedback, not ranking. A user who searched "ferritin" should see
 * the word "ferritin" lit up in the snippet even if our scorer matched on
 * a synonym. If we limited highlighting to the same path the scorer takes,
 * the highlighting would mislead more than it helps.
 */
export interface HighlightSegment {
  text: string
  match: boolean
}

export function highlightExcerpt(
  text: string,
  query: string
): HighlightSegment[] {
  if (!text) return []
  const tokens = tokenize(query)
  if (tokens.length === 0) return [{ text, match: false }]
  // Match the longest tokens first so "ferritin" wins over "fer" when both
  // hit. Use a single regex with alternation — one pass over the string,
  // and the engine takes the leftmost-longest match per browser convention.
  const sorted = [...new Set(tokens)].sort((a, b) => b.length - a.length)
  const escaped = sorted
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|")
  const re = new RegExp(`(${escaped})`, "gi")
  const out: HighlightSegment[] = []
  let lastIndex = 0
  for (const m of text.matchAll(re)) {
    if (m.index === undefined) continue
    if (m.index > lastIndex) {
      out.push({ text: text.slice(lastIndex, m.index), match: false })
    }
    out.push({ text: m[0], match: true })
    lastIndex = m.index + m[0].length
  }
  if (lastIndex < text.length) {
    out.push({ text: text.slice(lastIndex), match: false })
  }
  return out
}
